import { useState, useRef, useCallback, useEffect, MouseEvent } from 'react'

/**
 * Menu entries render as real anchors so the browser gives them hyperlink
 * behavior (right-click context menu, middle-click / ctrl-click / cmd-click to
 * open in a new tab). This returns true for the clicks the browser should
 * handle itself, so the caller knows to leave the event alone instead of
 * intercepting it for in-app routing.
 */
export function isBrowserHandledClick(event: MouseEvent): boolean {
  return (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  )
}

/**
 * The href a menu entry renders with: the processed link, so query parameters
 * the menu carries survive an "open in new tab". It is the same value in-app
 * navigation hands the router, so a new tab lands where a left click does. An
 * entry with no real destination gets no href, and so no link context menu.
 */
export function getMenuItemHref(
  link?: string,
  getProcessedLink?: (link: string) => string
): string | undefined {
  if (!link || link === '#') return undefined
  return getProcessedLink ? getProcessedLink(link) : link
}

/**
 * The click handler every menu entry shares: a plain left click routes in-app
 * through onSubItemClick, anything the browser should handle (see
 * isBrowserHandledClick) is left alone so the anchor's href takes over.
 */
export function followMenuLink(
  event: MouseEvent,
  link: string | undefined,
  getProcessedLink: ((link: string) => string) | undefined,
  onSubItemClick: ((link: string) => void) | undefined
): void {
  if (!onSubItemClick || !link) return
  // leave modified clicks (new tab, new window, download) to the browser
  if (isBrowserHandledClick(event)) return

  event.preventDefault()
  onSubItemClick(getProcessedLink ? getProcessedLink(link) : link)
}

/**
 * The expand arrow sits inside its entry's anchor. Its click handler already
 * stops the link being followed, but a middle click arrives as `auxclick`, not
 * `click`, so the arrow cancels that too rather than opening the parent's page.
 */
export function preventLinkFollow(event: MouseEvent): void {
  event.preventDefault()
}

export interface ScrollState {
  isAtTop: boolean
  isAtBottom: boolean
  canScroll: boolean
}

export interface UseScrollTrackingReturn {
  scrollRef: React.RefObject<HTMLDivElement>
  scrollState: ScrollState
  scrollToTop: () => void
  scrollToBottom: () => void
}

/**
 * Custom hook for tracking scroll position and providing scroll navigation
 * @param dependencies - Array of dependencies that should trigger a scroll position recheck
 * @returns Object containing scroll ref, state, and navigation functions
 */
export function useScrollTracking(
  dependencies: unknown[] = []
): UseScrollTrackingReturn {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollState, setScrollState] = useState<ScrollState>({
    isAtTop: true,
    isAtBottom: false,
    canScroll: false
  })

  // Scroll position tracking
  const checkScrollPosition = useCallback(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    const { scrollTop, scrollHeight, clientHeight } = scrollElement
    const canScroll = scrollHeight > clientHeight + 1 // Small buffer for precision
    const isAtTop = scrollTop <= 8 // Generous threshold for top
    const isAtBottom = scrollTop >= scrollHeight - clientHeight - 15 // Generous threshold for bottom

    // Only update state if something actually changed
    setScrollState(prevState => {
      if (
        prevState.canScroll === canScroll &&
        prevState.isAtTop === isAtTop &&
        prevState.isAtBottom === isAtBottom
      ) {
        return prevState // No change, don't trigger re-render
      }

      return {
        isAtTop,
        isAtBottom,
        canScroll
      }
    })
  }, [])

  // Scroll handlers
  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const scrollToBottom = useCallback(() => {
    const scrollElement = scrollRef.current
    if (scrollElement) {
      scrollElement.scrollTo({
        top: scrollElement.scrollHeight,
        behavior: 'smooth'
      })
    }
  }, [])

  // Set up scroll listener and initial check
  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    let animationFrameId: number | null = null

    const handleScroll = () => {
      // Only schedule one animation frame at a time
      if (animationFrameId) return

      animationFrameId = requestAnimationFrame(() => {
        checkScrollPosition()
        animationFrameId = null
      })
    }

    scrollElement.addEventListener('scroll', handleScroll, { passive: true })

    // Initial check
    const initialCheckTimeout = setTimeout(checkScrollPosition, 100)

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll)
      clearTimeout(initialCheckTimeout)
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [checkScrollPosition])

  // Recheck scroll position when dependencies change
  // Using a ref to avoid including checkScrollPosition in deps
  const checkScrollPositionRef = useRef(checkScrollPosition)
  checkScrollPositionRef.current = checkScrollPosition

  useEffect(() => {
    // Use a single timeout strategy to avoid conflicts
    const timeoutId = setTimeout(() => checkScrollPositionRef.current(), 100)

    return () => {
      clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies) // Only depend on external dependencies, not checkScrollPosition

  return {
    scrollRef,
    scrollState,
    scrollToTop,
    scrollToBottom
  }
}
